import { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import AnswerPreviewCard from "./AnswerPreviewCard";
import RoundIcon from "./RoundIcon";
import { useGetQuestionnaireRounds } from "../../../../hooks/query/issb";
import { constructQueryParamsString } from "../../../../utils";
import { limeFocus } from "./fieldStyles";
import { displayBucketKey, IMPACT_BUCKET, IMPACT_OPTION } from "../../../../utils/answerImpactOptions";
import { approvedAnswersAtRisk } from "../../../../utils/answerImpactFollowUp";

/*
  The two groups, in workflow order. There is no "In preparation" card: an answer citing these figures has
  been worked on by definition, so that bucket is shown as In progress (displayBucketKey). The definitions live in utils/answerImpactOptions.js because a
  datapoint's usage list shows a bucket too, and orange has to mean the same thing in both.

  The second is "Submitted or approved", NOT the mockup's "Approved": Ready for Approval means submitted and
  not yet signed off, so calling the group approved would say something untrue about a requirement nobody has
  approved. The SET is unchanged — it is exactly what option 3 would move back.

  `onlyWhenPresent` is this dialog's own concern, not part of the shared definition: a requirement with no
  workflow stage is a real legacy shape and must be visible when there is one — omitting it would show a total
  that quietly excludes a live answer — but a permanent "Stage unknown: 0" card on every dialog is noise.
*/
const BUCKETS = [
  IMPACT_BUCKET.IN_PROGRESS,
  IMPACT_BUCKET.SUBMITTED_OR_APPROVED,
  { ...IMPACT_BUCKET.UNKNOWN, onlyWhenPresent: true },
];

const OPTIONS = [
  {
    value: IMPACT_OPTION.NOTHING,
    label: "Change nothing",
    description:
      "Approve the new figures and leave every disclosure as it is. The affected disclosures are flagged as citing an older figure, so they can be re-checked by hand.",
  },
  {
    value: IMPACT_OPTION.DRAFT,
    label: "Draft updated disclosures for review",
    description:
      "Re-draft each affected disclosure with the new figures, keeping the existing wording. Nothing is applied and no stage moves — the drafts wait on each disclosure's AI Draft tab.",
  },
  {
    value: IMPACT_OPTION.APPLY_AND_REOPEN,
    label: "Update and review",
    description:
      "Replace each cited figure with its new value and return any approved disclosure to In progress for review. Only the citations change — the surrounding wording is kept as it is.",
  },
  {
    value: IMPACT_OPTION.APPLY,
    label: "Update disclosures in place",
    description:
      "Replace each cited figure with its new value and leave the status as is. Approved disclosures will be updated without further review.",
  },
];

/**
 * What to do about report answers built on figures that are about to change.
 *
 * Opened by `useAnswerImpactGate` when a workstream's owner approves it (ESG-4012) — the moment its changed
 * figures are published to answers and AI Review, so the choice is offered while there is still one. Before
 * this the only signal was a badge that appeared afterwards, and acting on it meant opening every affected
 * requirement by hand.
 *
 * Confirm sends the approval AND the chosen follow-up; Cancel abandons both. That is why "Change
 * nothing" is an option at all rather than the same thing as closing the dialog.
 */
export default function ManageChangedDataModal({
  isOpen,
  impact,
  onConfirm,
  onCancel,
  isBusy = false,
  /*
    Which options are wired. The rest are rendered anyway, disabled, so the choice on offer is visible rather
    than the feature looking absent — and so a half-shipped option cannot be selected by a stale client.
  */
  enabledOptions = [IMPACT_OPTION.NOTHING],
}) {
  const [option, setOption] = useState(IMPACT_OPTION.NOTHING);
  const [comment, setComment] = useState("");
  const [expanded, setExpanded] = useState(null);
  /*
    Option 4's second confirmation. It is the ONE option that can rewrite an approved disclosure and leave it
    still marked approved — no re-approval, no stage change, only an Activity row and a comment behind it. So
    the number of approved answers it would rewrite is put in front of the user and acknowledged, rather than
    reported after the fact. Reset whenever the option changes, so an acknowledgement cannot carry over.
  */
  const [acknowledged, setAcknowledged] = useState(false);

  // Memoised so the grouping below has a stable dependency: `impact?.requirements ?? []` is a fresh array
  // on every render, which would re-group on each keystroke in the comment box.
  const requirements = useMemo(() => impact?.requirements ?? [], [impact]);
  const counts = useMemo(() => {
    const folded = {};
    Object.entries(impact?.countsByBucket ?? {}).forEach(([key, count]) => {
      const shown = displayBucketKey(key);
      folded[shown] = (folded[shown] ?? 0) + (count ?? 0);
    });
    return folded;
  }, [impact]);
  const returnable = impact?.returnable ?? 0;

  const byBucket = useMemo(() => {
    const groups = {};
    requirements.forEach((requirement) => {
      const key = displayBucketKey(requirement.bucket);
      (groups[key] = groups[key] ?? []).push(requirement);
    });
    return groups;
  }, [requirements]);

  // Round names for the grouped list. The impact carries only a round id, never a name — the same lookup the
  // metric overlay's "Where it's used" makes. Fetched with the dialog so the page does not pay for it.
  const { data: roundsData } = useGetQuestionnaireRounds(constructQueryParamsString({ page: 0, size: 500 }), {
    enabled: !!isOpen,
  });
  const roundNameById = useMemo(
    () => Object.fromEntries((roundsData?.content ?? []).map((round) => [round.id, round.name])),
    [roundsData]
  );
  const expandedByRound = useMemo(() => {
    const groups = new Map();
    (byBucket[expanded] ?? []).forEach((requirement) => {
      const key = requirement.roundId ?? "none";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(requirement);
    });
    return Array.from(groups, ([roundId, items]) => ({ roundId, items }));
  }, [byBucket, expanded]);

  const visibleBuckets = BUCKETS.filter((bucket) => !bucket.onlyWhenPresent || (counts[bucket.key] ?? 0) > 0);

  const chooseOption = (next) => {
    setOption(next);
    setAcknowledged(false);
  };

  const atRisk = approvedAnswersAtRisk(impact, option);
  const blocked = atRisk > 0 && !acknowledged;

  const close = () => {
    setOption(IMPACT_OPTION.NOTHING);
    setComment("");
    setExpanded(null);
    setAcknowledged(false);
    onCancel?.();
  };

  const footerNote =
    option === IMPACT_OPTION.NOTHING
      ? "The new figures are saved. Each affected disclosure keeps its existing text and is flagged as citing an older figure."
      : option === IMPACT_OPTION.DRAFT
        ? `A draft is prepared for each of the ${requirements.length} affected disclosures. Nothing is applied and no stage moves — each draft waits on its disclosure's AI Draft tab.`
        : option === IMPACT_OPTION.APPLY_AND_REOPEN
          ? returnable > 0
            ? `The cited figures in each of the ${requirements.length} affected disclosures are replaced with the new values, and ${returnable} approved will return to In progress for review.`
            : /*
            Said plainly rather than shown as "and 0 will return to In Progress". With nothing submitted or
            approved this option does exactly what the one below it does, and a user choosing it to get a
            second look deserves to know they will not get one.
          */
              `The cited figures in each of the ${requirements.length} affected disclosures are replaced with the new values. Any approved disclosure is returned to In progress for review — none of these is approved yet, so no status changes.`
          : `The cited figures in each of the ${requirements.length} affected disclosures are replaced with the new values. No stage will move.`;

  return (
    <Modal
      isOpen={isOpen}
      // On the answers page Esc / overlay click go back to the decision — closing from there would cancel an
      // approval the user has not been shown the choice for again.
      onClose={expanded ? () => setExpanded(null) : close}
      size="4xl"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="rgba(5,31,35,.55)" />
      <ModalContent maxW="960px" borderRadius="16px">
        {/* Header in the extraction review's style: lime eyebrow, large title, one line of context. */}
        <ModalHeader pt={7} px={8} pb={4} borderBottom="1px solid" borderColor="gray.200">
          <Text fontSize="12px" fontWeight="700" letterSpacing="0.06em" color="limeShade.600" textTransform="uppercase">
            Manage changed data · before approving
          </Text>
          <Text fontSize="heading.l" fontWeight="700" color="darkTeal" mt={1}>
            {requirements.length} disclosure{requirements.length === 1 ? "" : "s"} use
            {requirements.length === 1 ? "s" : ""} the figures you are approving
          </Text>
        </ModalHeader>
        {expanded ? null : <ModalCloseButton top={6} right={6} />}

        <ModalBody key={expanded ? "answers" : "decision"} px={8} py={6}>
          {expanded ? (
            <AnswersView
              bucket={IMPACT_BUCKET[expanded]}
              groups={expandedByRound}
              roundNameById={roundNameById}
              onBack={() => setExpanded(null)}
            />
          ) : (
            <VStack align="stretch" spacing={6}>
              {/* Where the affected answers stand. Each card's top bar is the stage's own colour — the same
                bucket colours the metric overlay's usage list shows. A card opens its answers in the whole
                overlay (AnswersView), with Back returning here. */}
              <Box>
                <SimpleGrid columns={{ base: 1, md: visibleBuckets.length }} spacing={3}>
                  {visibleBuckets.map((bucket) => {
                    const count = counts[bucket.key] ?? 0;
                    return (
                      <Flex
                        key={bucket.key}
                        as={count > 0 ? "button" : "div"}
                        type={count > 0 ? "button" : undefined}
                        textAlign="left"
                        onClick={count > 0 ? () => setExpanded(bucket.key) : undefined}
                        align="center"
                        gap={4}
                        bg="darkTeal"
                        borderRadius="12px"
                        px={5}
                        py={4}
                        cursor={count > 0 ? "pointer" : "default"}
                        opacity={count > 0 ? 1 : 0.55}
                        transition="all 0.12s"
                        _hover={count > 0 ? { boxShadow: "0 0 0 2px var(--chakra-colors-lime)" } : undefined}
                      >
                        <Box
                          w="4px"
                          alignSelf="stretch"
                          borderRadius="full"
                          bg={BUCKET_BAR[bucket.key]}
                          flexShrink={0}
                        />
                        <Text fontSize="32px" fontWeight="800" color="white" lineHeight="1" minW="36px">
                          {count}
                        </Text>
                        <Box flex="1" minW={0}>
                          <Text fontSize="14px" fontWeight="700" color="white">
                            {bucket.label}
                          </Text>
                          <Text fontSize="12.5px" color="#A4B4B8">
                            {bucket.hint}
                          </Text>
                        </Box>
                        {count > 0 ? (
                          <HStack
                            spacing={1}
                            flexShrink={0}
                            bg="rgba(255,255,255,.1)"
                            color="white"
                            borderRadius="full"
                            px={3}
                            py={1}
                          >
                            <Text fontSize="12px" fontWeight="600" color="white">
                              View
                            </Text>
                            <CaretRight size={12} weight="bold" />
                          </HStack>
                        ) : null}
                      </Flex>
                    );
                  })}
                </SimpleGrid>
              </Box>

              {/* The choice, as the data mapping commit's decision cards: lime-outlined when selected. */}
              <Box>
                <SectionLabel>What should happen to these disclosures?</SectionLabel>
                <RadioGroup value={option} onChange={chooseOption}>
                  <VStack align="stretch" spacing={3}>
                    {OPTIONS.map((choice) => {
                      const disabled = !enabledOptions.includes(choice.value);
                      const selected = option === choice.value;
                      return (
                        <Box
                          key={choice.value}
                          as="label"
                          border="2px solid"
                          borderColor={selected ? "lime" : "gray.200"}
                          boxShadow={selected ? "0 0 0 3px var(--chakra-colors-lightLime)" : "none"}
                          borderRadius="12px"
                          p={4}
                          cursor={disabled ? "not-allowed" : "pointer"}
                          opacity={disabled ? 0.55 : 1}
                          transition="all 0.12s"
                          _hover={!disabled && !selected ? { borderColor: "gray.300" } : undefined}
                        >
                          <HStack align="start" spacing={3}>
                            <Radio value={choice.value} isDisabled={disabled} colorScheme="limeShade" mt="2px" />
                            <Box>
                              <HStack spacing={2}>
                                <Text fontWeight="700" color="darkTeal">
                                  {choice.label}
                                </Text>
                                {disabled ? (
                                  <Badge variant="subtle" colorScheme="gray" textTransform="none">
                                    coming soon
                                  </Badge>
                                ) : null}
                              </HStack>
                              <Text fontSize="13px" color="gray.600" mt={1}>
                                {choice.description}
                              </Text>
                            </Box>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </RadioGroup>
              </Box>

              <Box>
                <SectionLabel>Comment (optional)</SectionLabel>
                <Textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder={
                    option === IMPACT_OPTION.DRAFT
                      ? "Why the figures changed, and anything the re-draft should know."
                      : "Why the figures changed."
                  }
                  size="sm"
                  rows={3}
                  borderRadius="10px"
                  {...limeFocus}
                />
                {/*
                Added to every affected answer whichever option is chosen — "change nothing" included, where it
                is the only record the decision leaves. On a re-draft (option 2 — the only option that uses the AI) it
                ALSO reaches the AI as extra instructions, on top of the fixed "correct the figures, leave the
                wording alone" rule, which it cannot override.
              */}
                <Text fontSize="12px" color="gray.500" mt={1}>
                  Added to the Comments of {requirements.length === 1 ? "the" : "each"} affected disclosure.
                  {option === IMPACT_OPTION.DRAFT
                    ? " Also passed to the AI as extra guidance — it cannot override the rule that the wording stays as it is."
                    : ""}
                </Text>
              </Box>
            </VStack>
          )}
        </ModalBody>

        {expanded ? (
          <ModalFooter px={8} py={5} borderTop="1px solid" borderColor="gray.200">
            <Button
              variant="secondary"
              leftIcon={<CaretLeft size={14} weight="bold" />}
              onClick={() => setExpanded(null)}
            >
              Back to decision
            </Button>
          </ModalFooter>
        ) : (
          <ModalFooter
            flexDirection="column"
            alignItems="stretch"
            gap={3}
            px={8}
            py={5}
            borderTop="1px solid"
            borderColor="gray.200"
          >
            {/*
            What Confirm will do, stated before the click — the extraction review's footer notice. Says what is
            actually recorded, per requirement: there is no round-level activity log (an activity row is
            hard-bound to one question), so promising "one entry in Activity" would promise something the schema
            cannot hold.
          */}
            <Notice tone={option === IMPACT_OPTION.NOTHING ? "gray" : "lime"}>
              <Text fontSize="14px" color="gray.700">
                {footerNote}
              </Text>
            </Notice>

            {atRisk > 0 ? (
              <Notice tone="orange">
                <Text fontSize="14px" color="gray.700">
                  <Text as="span" fontWeight="700" color="orange.800">
                    {atRisk} approved disclosure{atRisk === 1 ? "" : "s"} will be updated and stay approved.
                  </Text>{" "}
                  Nobody re-approves {atRisk === 1 ? "it" : "them"} and the stage does not change, so the only record is
                  an entry on each disclosure&apos;s Activity. Choose &ldquo;Update and review&rdquo;
                  instead if {atRisk === 1 ? "it" : "they"} should be looked at again.
                </Text>
                <Checkbox
                  mt={2}
                  colorScheme="limeShade"
                  isChecked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                >
                  <Text fontSize="13px" color="gray.700">
                    I understand
                  </Text>
                </Checkbox>
              </Notice>
            ) : null}

            <HStack justify="flex-end" spacing={3} pt={1}>
              <Button variant="secondary" onClick={close} isDisabled={isBusy}>
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isBusy}
                isDisabled={blocked}
                onClick={() => onConfirm?.(option, comment)}
              >
                Approve and confirm
              </Button>
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}

/* Each bucket's side bar, in its stage colour (IMPACT_BUCKET.colorScheme), bright enough to read on dark teal. */
const BUCKET_BAR = {
  IN_PROGRESS: "blue.400",
  SUBMITTED_OR_APPROVED: "orange.400",
  UNKNOWN: "gray.300",
};

function bucketScheme(bucket) {
  return IMPACT_BUCKET[bucket]?.colorScheme ?? "gray";
}

/**
 * The answers in one stage, filling the overlay: grouped by round under the same headers as the metric
 * overlay's "Where it's used", each expandable to the full answer. Back returns to the decision with the
 * chosen option and comment untouched — they live in the parent.
 */
function AnswersView({ bucket, groups, roundNameById, onBack }) {
  const total = groups.reduce((sum, group) => sum + group.items.length, 0);
  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Button
          variant="link"
          size="sm"
          color="gray.600"
          leftIcon={<CaretLeft size={12} weight="bold" />}
          onClick={onBack}
          mb={3}
        >
          Back to decision
        </Button>
        <Text fontSize="18px" fontWeight="700" color="darkTeal">
          {bucket?.label ?? "Disclosures"} · {total} disclosure{total === 1 ? "" : "s"}
        </Text>
        <Text fontSize="13px" color="gray.500">
          Open a disclosure to read it as it stands now, before the new figures are applied.
        </Text>
      </Box>
      {groups.map((group) => (
        <Box key={group.roundId}>
          <Flex align="center" gap={3} bg="darkTeal" borderRadius="12px" px={4} py={3} mb={2}>
            <RoundIcon />
            <Box>
              <Text fontSize="10.5px" fontWeight="800" letterSpacing="0.08em" color="#B5E06C">
                ROUND
              </Text>
              <Text fontSize="13.5px" fontWeight="700" color="white">
                {group.roundId === "none" ? "No round" : roundNameById[group.roundId] || "Unknown round"}
              </Text>
              <Text fontSize="12px" color="#A4B4B8">
                {group.items.length} requirement{group.items.length === 1 ? "" : "s"} in this round using these figures
              </Text>
            </Box>
          </Flex>
          <VStack align="stretch" spacing={2}>
            {group.items.map((requirement) => (
              <AnswerPreviewCard
                key={requirement.questionId}
                questionId={requirement.questionId}
                question={requirement.question}
                header={
                  <HStack spacing={2} mb={1} flexWrap="wrap">
                    {requirement.issbReference ? (
                      <Text fontSize="11px" color="gray.400">
                        {requirement.issbReference}
                      </Text>
                    ) : null}
                    {requirement.stageName ? (
                      <Badge
                        variant="subtle"
                        colorScheme={bucketScheme(displayBucketKey(requirement.bucket))}
                        textTransform="none"
                      >
                        {requirement.stageName}
                      </Badge>
                    ) : null}
                    {requirement.superseded ? (
                      <HStack spacing={1}>
                        <Box w="6px" h="6px" borderRadius="50%" bg="orange.500" />
                        <Text fontSize="11px" fontWeight="600" color="orange.600">
                          Already citing an older figure
                        </Text>
                      </HStack>
                    ) : null}
                  </HStack>
                }
                footer={
                  (requirement.dataKeys ?? []).length > 0 ? (
                    <HStack spacing={1} mt={2} flexWrap="wrap">
                      <Text fontSize="11.5px" color="gray.500">
                        Uses
                      </Text>
                      {requirement.dataKeys.map((dataKey) => (
                        <Badge
                          key={dataKey}
                          variant="subtle"
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          color="gray.700"
                          fontWeight="500"
                          textTransform="none"
                        >
                          {dataKey}
                        </Badge>
                      ))}
                    </HStack>
                  ) : null
                }
              />
            ))}
          </VStack>
        </Box>
      ))}
    </VStack>
  );
}

function SectionLabel({ children }) {
  return (
    <Text fontSize="12px" fontWeight="700" letterSpacing="0.06em" color="gray.500" textTransform="uppercase" mb={3}>
      {children}
    </Text>
  );
}

/** A compact notice in the style of the extraction review's footer: white card, coloured left bar. */
function Notice({ tone = "gray", children }) {
  const bar = { gray: "gray.300", lime: "lime", orange: "orange.400" }[tone];
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderLeft="3px solid"
      borderLeftColor={bar}
      borderRadius="md"
      px={4}
      py={3}
    >
      {children}
    </Box>
  );
}
